import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Globe, TrendingUp, Languages } from 'lucide-react';
import NewsletterModal from '../UI/NewsletterModal';
import { useI18n } from '../../i18n/useI18n';

const categories = [
  { key: 'Geopolitics', icon: '🌍' },
  { key: 'Economy', icon: '💰' },
  { key: 'Climate', icon: '🌱' },
  { key: 'Health', icon: '🧬' },
  { key: 'Technology', icon: '🤖' },
  { key: 'Disasters', icon: '🚨' },
  { key: 'Migration', icon: '👥' }
];

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNewsletterOpen, setIsNewsletterOpen] = useState(false);
  const { t, locale, setLocale } = useI18n();

  const toggleLocale = () => {
    setLocale(locale === 'en' ? 'es' : 'en');
  };

  return (
    <>
      <header className="bg-white border-b-2 border-primary-900">
        {/* Top Bar */}
        <div className="bg-primary-900 text-white py-2">
          <div className="container-news">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-4">
                <span className="flex items-center">
                  <Globe className="h-4 w-4 mr-1" />
                  {t('header.global')}
                </span>
                <span className="hidden md:block">
                  {t('header.updated')} {new Date().toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-US')}
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={toggleLocale}
                  className="flex items-center space-x-1 hover:text-accent-400 transition-colors font-medium"
                  title={t('lang.label')}
                >
                  <Languages className="h-4 w-4" />
                  <span>{t('lang.switch')}</span>
                </button>
                <button
                  onClick={() => setIsNewsletterOpen(true)}
                  className="hover:text-accent-400 transition-colors"
                >
                  {t('nav.subscribe')}
                </button>
                <span className="hidden sm:flex items-center">
                  <TrendingUp className="h-4 w-4 mr-1" />
                  {t('header.zeroNoise')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Header */}
        <div className="container-news py-6">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex flex-col">
              <h1 className="font-playfair text-4xl font-black text-primary-900 tracking-tight">
                {t('site.name')}
              </h1>
              <p className="font-inter text-sm text-primary-600 tracking-widest uppercase">
                {t('site.tagline')}
              </p>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              <Link to="/" className="nav-link">
                {t('nav.latest')}
              </Link>
              {categories.map((category) => (
                <Link
                  key={category.key}
                  to={`/category/${encodeURIComponent(category.key)}`}
                  className="nav-link flex items-center"
                >
                  <span className="mr-1">{category.icon}</span>
                  {t(`cat.${category.key}`)}
                </Link>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6 text-primary-900" />
              ) : (
                <Menu className="h-6 w-6 text-primary-900" />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="lg:hidden mt-6 pt-6 border-t border-primary-200">
              <div className="flex flex-col space-y-4">
                <Link 
                  to="/" 
                  className="nav-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.latest')}
                </Link>
                {categories.map((category) => (
                  <Link
                    key={category.key}
                    to={`/category/${encodeURIComponent(category.key)}`}
                    className="nav-link flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span className="mr-2">{category.icon}</span>
                    {t(`cat.${category.key}`)}
                  </Link>
                ))}
              </div>
            </nav>
          )}
        </div>

        {/* Category Strip */}
        <div className="hidden md:block bg-primary-50 border-t border-primary-200">
          <div className="container-news py-3">
            <div className="flex items-center justify-center space-x-6">
              {categories.map((category) => (
                <Link
                  key={category.key}
                  to={`/category/${encodeURIComponent(category.key)}`}
                  className="flex items-center text-sm text-primary-700 hover:text-accent-600 transition-colors"
                >
                  <span className="mr-1">{category.icon}</span>
                  {t(`cat.${category.key}`)}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Newsletter Modal */}
      <NewsletterModal 
        isOpen={isNewsletterOpen} 
        onClose={() => setIsNewsletterOpen(false)} 
      />
    </>
  );
};

export default Header;